// @flow
import { Redirect } from 'react-router-dom';
import React from 'react';
import _ from 'lodash';
import styled from "styled-components";
import { connect } from "react-redux";
import moment from "moment/moment";
import * as actions from "../../modules/actions";
import * as selectors from "../../modules/selectors";
import { league_tooltips } from '../../helpers';
import type { TPLeague } from "../../modules/types/league";
import { below } from '../../assets/css/media';
import {
	ButtonAlert,
	ConfirmationModal,
	BorderedIcon,
	LeagueAvatar,
	OneColumnLayout,
	PageTitle,
	AdsContainer,
	Ad,
} from "../../components";


import colors from "../../assets/css/colors";

import Settings from '../../components/Icons/Settings';
import People from '../../components/Icons/People';
import Ball from '../../components/Icons/Ball';
import Trade from '../../components/Icons/Trade';

import { BYE_ROUNDS } from '../../helpers/index';
import { RemoveLeagueSection } from './leagueSettings/LeaguesSettingsForm';
import LeagueItem from './leagueAbout/leagueItem';

const AboutPageStyled = styled.div`
	box-shadow: ${colors.box_shadow};
	margin: 0 0 0;
	padding: 0 0 1em;

	button {
		display: block;
		width: 290px;
		margin: 0 auto;
	}

	svg {
		width: 1.1em;
	}
`;

const PageTitleStyled = styled(PageTitle)`
	margin-top: 1em;
	margin-left: 1em;
`;

const AvatarWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background-color: #FFFFFF;
	margin-bottom: 35px;
	box-shadow: 0 1px 0 0 rgba(0,0,0,0.25);
	color: ${colors.nrlDarkGreen};
	padding: 20px 0;

	picture {
		display: flex;
	    width: 100px;
	    height: 100px;
	}
`;

const DataWrapper = styled.div`
	max-width: 484px;
	margin: 0 auto;

	label {
		margin: 0 0 0.2em;
	}
	${below.tablet`
			padding: 0 1em;
		`}	
`;

const SubTitle = styled.h4`
	display: flex;
	align-items: center;
	color: ${colors.darkGrey};
	font-weight: bold;
	margin: 30px 0 20px;

	div {
		margin-right: .5em;
	}
`;

const LeagueName = styled.h3`
	color: ${colors.nrlDarkGreen};
	font-size: 20px;
	line-height: 1;
	font-weight: bold;
	opacity: ${({ disabled }) => disabled ? '.15' : '1'};
    max-width: 300px;
    overflow: hidden;
	text-overflow: ellipsis;
	margin: 0.7em 0 0.6em;
`;

const LeagueCommissioner = styled.h5`
	font-weight: normal;
	font-size: 12px;
	line-height: 1;
	color: #C0C0C0;
`;

type Props = {
	showDraftLeague: typeof actions.leagueDraft.showDraftLeague,
	leaveDraftLeague: typeof actions.leagueDraft.leaveDraftLeague,
	showTeamsDraftLeague: typeof actions.leagueDraft.showTeamsDraftLeague,
	match: {
		params: {
			league_id: number,
		}
	},
	league_show: {
		error: string,
		league_id: number,
	},
	league: TPLeague,
	commissioner_name: string,
	leave_league_success: boolean
}

type State = {
	show_leave_modal: boolean,
	left_the_league: boolean,
};

class LeagueAboutComponent extends React.Component<Props, State> {
	state = {
		show_leave_modal: false,
		left_the_league: false,
	};

	constructor(props, state) {
		super(props, state);

		_.bindAll(this, [
			'leaveThisLeague',
			'confirmLeaveleague',
			'closeLeaveModal',
		]);
	}

	componentDidMount() {
		window.scrollTo(0, 0);

		const {
			match: {
				params: {
					league_id,
				}
			},
			showDraftLeague,
			showTeamsDraftLeague
		} = this.props;

		showDraftLeague({ id: league_id });
		showTeamsDraftLeague({ league_id });
	}

	confirmLeaveleague() {
		const { leaveDraftLeague, league: { id } } = this.props;
		leaveDraftLeague({ league_id: id });
		this.setState({ left_the_league: true });
	}

	leaveThisLeague() {
		this.setState({ show_leave_modal: true });
	}

	closeLeaveModal() {
		this.setState({ show_leave_modal: false });
	}

	get leave_modal() {
		return (
			<ConfirmationModal
				header_text='League About'
				body_text='Are you sure you want to leave this league ?'
				confirmClick={this.confirmLeaveleague}
				cancelClick={this.closeLeaveModal}
				closeClick={this.closeLeaveModal}
			/>
		);
	}

	get league_settings() {
		const { league } = this.props;
		return (
			<div>
				<LeagueItem
					name='League Name'
					content={league.name}
					tooltip={league_tooltips.size}
				/>
				<LeagueItem
					name='League Size'
					content={league.size}
					tooltip={league_tooltips.size}
				/>
				<LeagueItem
					name='League Start Round'
					content={league.start_round}
					tooltip={league_tooltips.start_round}
				/>
				<LeagueItem
					name='Privacy Settings'
					content={league.privacy ? 'Public' : 'Private'}
					tooltip={league_tooltips.privacy}
				/>
				<LeagueItem
					name='Draft date and time'
					content={moment(league.draft_start).format('DD/MM/YY HH:mm')}
					tooltip={league_tooltips.draft_start}
				/>
			</div>
		);
	}

	get advanced_settings() {
		const { league } = this.props;

		return(
			<div>
				<LeagueItem
					name='League Finals Series'
					content={league.finals ? 'Yes' : 'No'}
					tooltip={league_tooltips.finals}
				/>

				{league.finals ? (
					<LeagueItem
						name='League Finals Format'
						content={'TOP '+league.finals_format}
						tooltip={league_tooltips.finals_format}
					/>
				): null}
				<LeagueItem
					name='Play Opponents'
					content={league.play_times}
					tooltip={league_tooltips.play_times}
				/>
				{BYE_ROUNDS.length ? (
					<LeagueItem
						name='Bye Rounds'
						content={league.bye_rounds ? 'On' : 'Off'}
						tooltip={league_tooltips.bye_rounds}
					/>
				) : null}

			</div>
		);
	}
	draft_turn_value(turn_in_seconds) {
		const options = {
			'60': '60 SECONDS',
			'120': '2 minutes',
			'300': '5 minutes',
			'900': '15 minutes',
			'3600': '60 minutes',
			'43200': '12 hours',
			'86400': '24 hours'

		};

		return _.get(options, turn_in_seconds);
	};


	get draft_settings() {
		const { league } = this.props;

		return(
			<div>
				<LeagueItem
					name='Draft type'
					content={league.draft_type}
					tooltip={league_tooltips.draft_type}
				/>
				<LeagueItem
					name='Draft order'
					content={league.draft_order}
					tooltip={league_tooltips.draft_order }
				/>
				<LeagueItem
					name='Draft turn time'
					content={this.draft_turn_value(league.draft_turn)}
					tooltip={league_tooltips.draft_turn}
				/>
			</div>
		);
	}

	get game_play_settings() {
		const { league } = this.props;

		return(
			<div>
				<LeagueItem
					name='Squad Size & Layout'
					content={league.squad_size}
					tooltip={league_tooltips.squad_size}
				/>
				<LeagueItem
					name='Captains'
					content={league.captains ? 'On' : 'Off'}
					tooltip={league_tooltips.captains}
				/>
				<LeagueItem
					name='Emergencies'
					content={league.emergency ? 'On' : 'Off'}
					tooltip={league_tooltips.emergency}
				/>
				<LeagueItem
					name='Rolling Lockout'
					content={league.lockout ? 'On' : 'Off'}
					tooltip={league_tooltips.lockout}
				/>
			</div>
		);
	}

	approve_trade_options(item) {
		const options = {
			league: 'League Approved',
			commissioner: 'Commissioner Approved',
			free: 'Free for all',
		};

		return _.get(options, item);
	}

	get trades_restricted_settings() {
		const { league } = this.props;

		return(
			<div>
				<LeagueItem
					name='Restricted Free Agents'
					content={league.trade_waivers ? 'On' : 'Off'}
					tooltip={league_tooltips.trade_waivers}
				/>
				<LeagueItem
					name='Restricted Free Agent Time Period'
					content={league.waiver_period === 1 ? '1 day' : '2 days'}
					tooltip={league_tooltips.waiver_period}
				/>
				<LeagueItem
					name='Restricted Free Agent Order Policy'
					content={league.waiver_order === 1
						? 'Last Pick to Bottom' : 'Reverse Ladder Order'}
					tooltip={league_tooltips.waiver_order}
				/>
				<LeagueItem
					name='Restricted Free Agent Privacy'
					content={league.rfa_privacy}
					tooltip={league_tooltips.rfa_privacy}
				/>
				<LeagueItem
					name='Trade Approval Options'
					content={this.approve_trade_options(league.approve_trade)}
					tooltip={league_tooltips.approve_trade}
				/>
			</div>
		);
	}

	render() {
		const {
			league,
			league_show,
			leave_league_success
		} = this.props;

		const {
			show_leave_modal,
			left_the_league,
		} = this.state;

		return (
			<div>
				{league_show.error ? <Redirect to='leagues' /> : null}
				{left_the_league && leave_league_success ? <Redirect to='leagues' /> : null}
				{show_leave_modal ? this.leave_modal : null}
				<AdsContainer>
					<Ad
						adUnit='about'
						size='top'
					/>
				</AdsContainer>
				<OneColumnLayout className="league-about">
					<PageTitleStyled>About League</PageTitleStyled>
					<AboutPageStyled>
						<AvatarWrapper>
							<LeagueAvatar league={league} size="100px" />
							<LeagueName>{league.name}</LeagueName>
							<LeagueCommissioner>{this.props.commissioner_name}</LeagueCommissioner>
						</AvatarWrapper>
						<DataWrapper>
							{this.league_settings}
							<SubTitle>
								<BorderedIcon background='#C0C0C0' hide_border={true}>
									<Settings color='#ffffff' size="2" />
								</BorderedIcon>
								Advanced Settings
							</SubTitle>
							{this.advanced_settings}

							<SubTitle>
								<BorderedIcon background='#C0C0C0' hide_border={true}>
									<People color='#ffffff' size="2" />
								</BorderedIcon>
								Draft Options
							</SubTitle>
							{this.draft_settings}

							<SubTitle>
								<BorderedIcon background='#C0C0C0' hide_border={true}>
									<Ball color='#ffffff' size="2" />
								</BorderedIcon>
								Game Play
							</SubTitle>
							{this.game_play_settings}

							<SubTitle>
								<BorderedIcon background='#C0C0C0' hide_border={true}>
									<Trade color='#ffffff' size="2" />
								</BorderedIcon>
								Trades & Restricted Free Agents
							</SubTitle>
							{this.trades_restricted_settings}

						</DataWrapper>
					</AboutPageStyled>
					{league.status === 'scheduled' && league.draft_status === 'scheduled' ? (
						<RemoveLeagueSection>
							<h4>
						Would you like to leave this league?
							</h4>
							<p>
						This action is permanent
							</p>
							<ButtonAlert onClick={this.leaveThisLeague}>
							LEAVE THIS LEAGUE
							</ButtonAlert>
						</RemoveLeagueSection>
					) : null}
				</OneColumnLayout>
				<AdsContainer>
					<Ad
						adUnit='about'
						size='bottom'
					/>
				</AdsContainer>
			</div>
		);
	}
}

const mapStateToProps = (state, props) => ({
	league_show: state.leagues.show,
	league: selectors.leagueDraft.getLeague(state, props),
	league_leave: state.leagues.leave,
	leave_league_success: state.leagues.leave.success,
	commissioner_name: selectors.leagueDraft.getCommissionerName(state, props)
});

const mapDispatchToProps = {
	showDraftLeague: actions.leagueDraft.showDraftLeague,
	leaveDraftLeague: actions.leagueDraft.leaveDraftLeague,
	showTeamsDraftLeague: actions.leagueDraft.showTeamsDraftLeague,
};

export const LeagueAbout = connect(
	mapStateToProps,
	mapDispatchToProps
)(LeagueAboutComponent);

export default LeagueAbout;


// WEBPACK FOOTER //
// ./src/pages/league/LeagueAbout.js